/**
 * Shared Job Validation Module
 *
 * This module provides centralized validation logic for job imports across both:
 * - ImportTable.tsx (bulk import with programmatic validation)
 * - SingleImportForm.tsx (single entry with react-hook-form)
 *
 * All validation rules, error messages, and business logic are consolidated here
 * to ensure consistency across the application.
 */

import type { Deal } from "@domain/CRM/deal";
import { geometryHasCoordinates } from "@domain/Geo/geometry";

import type { Job } from "../types/Job";
import { DefaultImportTableColumns, ImportTableColumn } from "@domain/Settings";
import { CREATE_NEW_SALESPERSON } from "@domain/constants";
import { isValidPhoneNumber } from "libphonenumber-js/min";
import type { ValidationContext, ValidationResult } from "../types";
import { columnDisplayNames } from "../types";

// #region DEFINITIONS

const regex = {
	positiveInteger: /^[0-9][0-9]*$/, // Positive integers only (>0)
	positiveDecimal: /^(?!(?:0+)(?:\.0+)?$)\d+(\.\d+)?$/, // Positive decimals only (>0), disallows 0 or 0.0; allows 0.1, 05.5, 5, 5.123
	email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
};

// Use Map for O(1) regex pattern lookup with explicit typing
const NUMERIC_PATTERNS = new Map<ImportTableColumn, RegExp>([
	["frequencyOfMicros", regex.positiveInteger],
	["depth", regex.positiveDecimal],
	["density", regex.positiveDecimal],
]);

const invalidMessage = {
	number: "Invalid number",
	phone: "Invalid phone number",
	email: "Invalid email address",
};

/**
 * A single validation problem on one field of one job.
 *
 * `blocking` is `isFieldVisible(field, jobContext)` — only blocking issues
 * gate submission. Errors on fields the user can't see/edit (hidden by company
 * settings, not required) are still surfaced for display but never block.
 */
export interface JobIssue {
	field: ImportTableColumn;
	message: string;
	kind: "missing" | "invalid";
	blocking: boolean;
}

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
export const ValidationMessages: Record<ImportTableColumn, { required?: string; invalid?: string }> = {
	// Basic Information
	grower: {
		required: "Grower Name is required",
	},
	farm: {
		required: "Farm Name is required",
	},
	field: {
		required: "Field name is required",
	},

	// Attachments
	boundaryGeoJSON: {
		required: "Boundary File is required",
	},
	pointsGeoJSON: {
		required: "Points File is required",
	},
	zonesGeoJSON: {
		required: "Zone File is required",
	},
	fieldNotes: {
		required: "Field Notes are required",
	},

	// Order
	isReadyToSample: {
		required: "Ready to Sample is required",
	},
	dateFieldReadyEstimated: {
		required: "Estimated field ready date is required",
	},
	fieldPriority: {
		required: "Field priority is required",
	},
	collectBoundary: {
		required: "Collect Boundary is required",
	},
	submissionNotes: {
		required: "Submission Notes are required",
	},

	// Deal
	density: {
		required: "Density is required",
		invalid: invalidMessage.number,
	},
	depth: {
		required: "Depth is required",
		invalid: invalidMessage.number,
	},
	labName: {
		required: "Lab Name is required",
	},
	testPackage: {
		required: "Test Package is required",
	},
	frequencyOfMicros: {
		required: "Frequency of Micros is required",
		invalid: invalidMessage.number,
	},
	deal: {
		required: "Deal Configuration is required",
	},

	// Lab Info
	labInstructions: {
		required: "Lab Instructions are required",
	},
	microsList: {
		required: "Samples with Addon Tests are required",
	},
	lastCrop: {
		required: "Last crop is required",
	},
	nextCrop: {
		required: "Next crop is required",
	},
	yieldGoal: {
		required: "Yield Goal is required",
	},

	// Contacts
	branch: {
		required: "Branch is required",
	},
	salesperson: {
		required: "Salesperson is required",
	},
	salespersonMobilePhone: {
		required: "Salesperson phone number is required",
		invalid: invalidMessage.phone,
	},
	growerPhoneNumber: {
		required: "Grower phone number is required",
		invalid: invalidMessage.phone,
	},
	submitterName: {
		required: "Submitter Name is required",
	},
	submitterEmail: {
		required: "Submitter Email is required",
		invalid: invalidMessage.email,
	},

	// Sync Codes
	eventId: {
		required: "Event/Sync Code is required",
	},
	labSubmissionCode: {
		required: "Lab Submission Code is required",
	},
	fieldId: {
		required: "Field ID is required",
	},
	externalId: {
		required: "External ID is required",
	},
	externalFarmId: {
		required: "External Farm ID is required",
	},
	externalLabId: {
		required: "External Lab ID is required",
	},

	// Misc
	isTestJob: {
		required: "Test Job flag is required",
	},
};

// ==========================================================================================
// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
// Maintain the same order in all places for consistency.
// ==========================================================================================
type FieldProperty = keyof Job | keyof Job["files"] | "salespersonMobilePhone";
export const FIELD_TO_PROPERTY_MAP: Record<ImportTableColumn, FieldProperty> = {
	// Basic Information
	grower: "importGrowerName",
	farm: "importFarmName",
	field: "importFieldName",

	// Attachments
	boundaryGeoJSON: "boundaryGeoJSON",
	pointsGeoJSON: "pointsGeoJSON",
	zonesGeoJSON: "zonesGeoJSON",
	fieldNotes: "fieldNotes",

	// Order
	isReadyToSample: "isReadyToSample",
	dateFieldReadyEstimated: "dateFieldReadyEstimated",
	fieldPriority: "fieldPriority",
	collectBoundary: "collectBoundary",
	submissionNotes: "submissionNotes",

	// Deal
	density: "importDensity",
	depth: "importDepth",
	labName: "labName",
	testPackage: "externalTestPackageId",
	frequencyOfMicros: "externalFrequencyOfMicros",
	deal: "dealIds",

	// Lab Info
	labInstructions: "labInstructions",
	microsList: "microsList",
	lastCrop: "lastCrop",
	nextCrop: "nextCrop",
	yieldGoal: "yieldGoal",

	// Contacts
	branch: "branchIds",
	salesperson: "salespersonIds",
	salespersonMobilePhone: "salespersonMobilePhone",
	growerPhoneNumber: "growerPhoneNumber",
	submitterName: "submitterName",
	submitterEmail: "submitterEmail",

	// Sync Codes
	eventId: "eventId",
	labSubmissionCode: "labSubmissionCode",
	fieldId: "fieldId",
	externalId: "externalId",
	externalFarmId: "externalFarmId",
	externalLabId: "externalLabId",

	// Misc
	isTestJob: "isTestJob",
};

// #endregion

// #region HELPERS

/** Get or compute cached normalized sync methods */
function getSyncMethodCache(context: ValidationContext): { method1: string; method2: string } {
	if (!context._syncMethodCache) {
		context._syncMethodCache = {
			method1: context.deal?.syncMethod?.toString().toLowerCase() ?? "",
			method2: context.deal?.syncMethod2?.toString().toLowerCase() ?? "",
		};
	}
	return context._syncMethodCache;
}

function syncMethodContains(value: string, context: ValidationContext) {
	const cache = getSyncMethodCache(context);
	return cache.method1.includes(value) || cache.method2.includes(value);
}

/** The field's required flag as configured, before any per-field business rule is applied. */
function baseRequiredPerCompany(fieldType: ImportTableColumn, context: ValidationContext): boolean {
	const setting = context.company?.portalSettings?.importTableColumns?.[fieldType];
	if (setting) return setting.required;
	return DefaultImportTableColumns[fieldType]?.required ?? false;
}

/**
 * A per-field required-ness rule. `base` is the configured flag from `baseRequiredPerCompany`;
 * the rule decides what the configuration means for that field in this context.
 */
type RequiredRule = (base: boolean, context: ValidationContext) => boolean;

/** Agrian carries its own event identity, so the column is never required there. */
const requiredEventId: RequiredRule = (base, context) => {
	if (context.integration == "Agrian") return false;
	const company = context.company;
	const filesRequired =
		isFieldRequired("boundaryGeoJSON", { company }) ||
		isFieldRequired("pointsGeoJSON", { company }) ||
		isFieldRequired("zonesGeoJSON", { company });
	return base || (filesRequired && syncMethodContains("event", context));
};

/** Only demanded once the job is actually marked ready — and only when that column is in play. */
const requiredFieldPriority: RequiredRule = (base, context) => {
	if (!isFieldVisible("isReadyToSample", context)) return base;
	return base && (context.job?.isReadyToSample ?? false);
};

/** Zone deals need zones, unless the deal composites them away. */
const requiredZonesGeoJSON: RequiredRule = (base, context) =>
	base || (context.deal?.sitesType === "Zone" && !context.deal?.isComposite && !context.deal?.isMultiComposite);

/** Points come from an attachment, the deal's own flag, or an immutable/grid sites type. */
const requiredPointsGeoJSON: RequiredRule = (base, context) =>
	base ||
	!!context.isPointsAttached ||
	!!context.deal?.isPtsRequired ||
	(syncMethodContains("immut", context) && context.deal?.sitesType === "Grid" && context.integration != "Agrian") ||
	context.deal?.sitesType === "ModGrid";

/** Required once the company has branches at all, or the lab account is branch-scoped. */
const requiredBranch: RequiredRule = (base, context) => {
	const hasBranches = (context.company?.branchIds?.length ?? 0) > 0;
	const isLabBranch = (context.deal?.labAccountNumber ?? "").toLowerCase().includes("branch");
	return base || hasBranches || isLabBranch;
};

/**
 * Fields whose required-ness depends on more than the company setting. Anything absent from this
 * table is required exactly as configured.
 *
 * IMPORT_FIELDS: a new field only belongs here if it has conditional required-ness.
 */
const REQUIRED_RULES: Partial<Record<ImportTableColumn, RequiredRule>> = {
	eventId: requiredEventId,
	labSubmissionCode: (base, context) => base || syncMethodContains("lab", context),
	fieldPriority: requiredFieldPriority,
	microsList: (base, context) => base && (context.hasPointsFile ?? false),
	zonesGeoJSON: requiredZonesGeoJSON,
	pointsGeoJSON: requiredPointsGeoJSON,
	salespersonMobilePhone: (base, context) => base && (context.isCreatingNewSalesperson ?? false),
	branch: requiredBranch,
};

/** Check if a field is required based on company settings and business logic */
export function isFieldRequired(fieldType: ImportTableColumn, context: ValidationContext): boolean {
	const base = baseRequiredPerCompany(fieldType, context);
	const rule = REQUIRED_RULES[fieldType];
	return rule ? rule(base, context) : base;
}

/** Check if a field is visible based on company settings and business logic */
export function isFieldVisible(fieldType: ImportTableColumn, context: ValidationContext): boolean {
	const company = context.company;

	// Get base visibility from company settings or defaults
	let isVisiblePerCompany = true;
	if (company?.portalSettings?.importTableColumns?.[fieldType]) {
		isVisiblePerCompany = company.portalSettings.importTableColumns[fieldType].visible;
	} else {
		isVisiblePerCompany = DefaultImportTableColumns[fieldType]?.visible ?? true;
	}

	// Even if not explicitly visible, required fields must be shown
	const isVisible = isVisiblePerCompany || isFieldRequired(fieldType, context);

	// Apply business logic for special fields
	switch (fieldType) {
		case "eventId":
			return isVisible || syncMethodContains("event", context);

		case "fieldPriority":
			return isVisible || isFieldVisible("isReadyToSample", context);

		case "microsList":
			return isFieldRequired(fieldType, context) || (isVisiblePerCompany && (context.hasPointsFile ?? false));

		case "isTestJob":
			return isVisible || (context.user?.isAdmin ?? false);

		default:
			return isVisible;
	}
}

/** Check if a value is considered empty for a given field type */
export function isEmpty(value: any, fieldType: ImportTableColumn): boolean {
	if (value === undefined || value === null) {
		return true;
	} else if (isBooleanField(fieldType)) {
		// For boolean fields, false is a valid value, only undefined/null is empty
		return false;
	} else if (isNumericField(fieldType)) {
		return value === "";
	} else if (isArrayField(fieldType)) {
		return Array.isArray(value) && value.length === 0;
	} else if (typeof value === "string") {
		return value.trim() === "";
	} else {
		return false;
	}
}

// Pre-computed Sets for O(1) field type lookups (replacing O(n) Array.includes())
const ARRAY_FIELDS = new Set<ImportTableColumn>(["salesperson", "branch", "deal"]);
const BOOLEAN_FIELDS = new Set<ImportTableColumn>(["isReadyToSample", "collectBoundary", "isTestJob"]);
const NUMERIC_FIELDS = new Set<ImportTableColumn>(["density", "depth", "frequencyOfMicros"]);

export function isArrayField(fieldType: ImportTableColumn): boolean {
	return ARRAY_FIELDS.has(fieldType);
}

export function isBooleanField(fieldType: ImportTableColumn): boolean {
	return BOOLEAN_FIELDS.has(fieldType);
}

export function isNumericField(key: ImportTableColumn): boolean {
	return NUMERIC_FIELDS.has(key);
}
// #endregion

// #region VALIDATION FUNCTIONS

/**
 * Validates a single field value based on its type and context
 * This is the main validation function used by both components
 */
export function validateField(fieldType: ImportTableColumn, value: any, context: ValidationContext): ValidationResult {
	// Boolean fields are always valid - they have only two states (true/false), both valid
	if (isBooleanField(fieldType)) {
		return { isValid: true };
	}

	const isRequired = isFieldRequired(fieldType, context);
	const valueIsEmpty = isEmpty(value, fieldType);

	// Handle empty values: valid if not required, error if required
	if (valueIsEmpty) {
		return isRequired
			? {
					isValid: false,
					error: ValidationMessages[fieldType]?.required || `${columnDisplayNames[fieldType]} is required`,
				}
			: { isValid: true };
	}

	// Field-specific validation
	switch (fieldType) {
		case "frequencyOfMicros":
		case "depth":
		case "density":
			return validateNumeric(value, fieldType);

		case "growerPhoneNumber":
		case "salespersonMobilePhone":
			return validatePhone(value, context, fieldType);

		case "submitterEmail":
			return validateEmail(value, fieldType);

		case "salesperson":
			return validateSalesperson(value, context.job);

		default:
			// For all other fields, if they're required and we got here,
			// the isEmpty check above handles validation
			return { isValid: true };
	}
}

/** Validate numeric fields (density, depth, frequencyOfMicros) */
function validateNumeric(value: any, fieldType: ImportTableColumn): ValidationResult {
	const pattern = NUMERIC_PATTERNS.get(fieldType) ?? /.*/;
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid number";

	// Check for empty values (but allow 0)
	if (value === undefined || value === null || value === "") {
		return { isValid: false, error: invalidMessage };
	}

	// Convert to number and check if valid
	const numValue = Number(value);
	if (isNaN(numValue)) {
		return { isValid: false, error: invalidMessage };
	}

	// Check pattern (validates positive numbers, decimals, etc.)
	const stringValue = value.toString();
	if (!pattern.test(stringValue)) {
		return { isValid: false, error: invalidMessage };
	}

	return { isValid: true };
}

/** Validate phone numbers */
function validatePhone(
	value: any,
	context: ValidationContext,
	fieldType: "growerPhoneNumber" | "salespersonMobilePhone",
): ValidationResult {
	const isRequired = isFieldRequired(fieldType, context);
	const requiredMessage = ValidationMessages[fieldType].required || "Phone number is required";
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid phone number";

	if (!isRequired && isEmpty(value, fieldType)) {
		return { isValid: true };
	}

	if (isRequired && isEmpty(value, fieldType)) {
		return {
			isValid: false,
			error: requiredMessage,
		};
	}

	// If we have a value, validate the phone format
	if (value) {
		const phoneString = value.toString();
		const valid = isValidPhoneNumber(phoneString, "US");

		if (!valid) {
			return {
				isValid: false,
				error: invalidMessage,
			};
		}
	}

	return { isValid: true };
}

/** Pure email-format check. Shared by validation and import job seeding. */
export function isValidEmail(value: string): boolean {
	return regex.email.test(value);
}

/** Validate email addresses */
function validateEmail(value: any, fieldType: ImportTableColumn): ValidationResult {
	const invalidMessage = ValidationMessages[fieldType].invalid || "Invalid email address";

	if (value && typeof value === "string") {
		if (!isValidEmail(value)) {
			return {
				isValid: false,
				error: invalidMessage,
			};
		}
	}

	return { isValid: true };
}

/** Validate salesperson (including new salesperson creation) */
function validateSalesperson(_value: any, job?: Job): ValidationResult {
	// Note: Empty array check is handled by isEmpty() in validateField()
	// This function only validates the content when a value is present

	// Check for new salesperson validation
	if (job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON)) {
		if (!job.newSalesperson?.name || job.newSalesperson.name.trim() === "") {
			return { isValid: false, error: "New salesperson name is required" };
		}
	}

	return { isValid: true };
}

const FileTypes = ["boundaryGeoJSON", "pointsGeoJSON", "zonesGeoJSON", "fieldNotes"] as const;
export type FileType = (typeof FileTypes)[number];
const FILE_TYPES_SET = new Set<string>(FileTypes);

// Pre-computed field types without files for validateJobs (avoids Object.keys() allocation per iteration)
const FIELD_TYPES_WITHOUT_FILES = (Object.keys(FIELD_TO_PROPERTY_MAP) as ImportTableColumn[]).filter(
	(ft) => !FILE_TYPES_SET.has(ft),
);

/** Check if a job has *usable* GeoJSON data for a given file type.
 *
 * Geometry-aware on purpose: an integration can hand back a feature whose
 * geometry has empty coordinates (Agrian's empty boundary, the 2026-08-06
 * outage). Counting that as "file present" let a boundary-less job sail past
 * the required-file validation — a job with only empty geometry has no file. */
export function hasGeoJSON(job: Job, fileType: FileType): boolean {
	if (fileType === "fieldNotes") return false;
	const geojson = job.files?.[fileType] as GeoJSON.FeatureCollection | undefined;
	return (
		!!geojson &&
		Array.isArray(geojson.features) &&
		geojson.features.some((feature) => geometryHasCoordinates(feature.geometry))
	);
}

/** Validate file upload fields */
export function validateFile(fileType: FileType, job: Job, context: ValidationContext): ValidationResult {
	const isRequired = isFieldRequired(fileType as ImportTableColumn, context);

	if (!isRequired) {
		return { isValid: true };
	}

	if (!hasGeoJSON(job, fileType)) {
		return {
			isValid: false,
			error:
				ValidationMessages[fileType as ImportTableColumn]?.required || `${columnDisplayNames[fileType]} is required`,
		};
	}

	return { isValid: true };
}

export const ZONE_CORE_ERROR = "Zone deals require cores";
export const GRID_CENTROID_ERROR = "Grid deals require centroids";

// #endregion

// #region BATCH VALIDATION (for ImportTable)

/**
 * Creates a standardized validation error key
 * @param fieldType - The field/column identifier
 * @param jobId - The job ID
 * @returns Error key in format: `${fieldType}-${jobId}`
 *
 * @example
 * getValidationErrorKey("density", "job-123") // "density-job-123"
 * getValidationErrorKey("grower", "job-456") // "grower-job-456"
 */
export function getValidationErrorKey(fieldType: ImportTableColumn, jobId: string): string {
	return `${fieldType}-${jobId}`;
}

/**
 * Parses a validation error key to extract the field type
 * @param errorKey - The error key in format `${fieldType}-${jobId}`
 * @returns The field type, or undefined if the key is malformed
 *
 * @example
 * parseValidationErrorKey("density-job-123") // "density"
 * parseValidationErrorKey("grower-job-456") // "grower"
 */
export function parseValidationErrorKey(errorKey: string): ImportTableColumn | undefined {
	if (!errorKey.includes("-")) {
		return undefined;
	}
	const parts = errorKey.split("-");
	if (parts.length < 2) {
		return undefined;
	}
	return parts[0] as ImportTableColumn;
}

/** Pre-compute the deal lookup map once for O(1) per-job access. */
function buildDealMap(context: ValidationContext): Map<string, Deal> {
	const dealMap = new Map<string, Deal>();
	if (context.allDeals) {
		for (const deal of context.allDeals) {
			dealMap.set(deal.id, deal);
		}
	}
	return dealMap;
}

/**
 * Assemble the per-job validation context (deal lookup + derived flags).
 * `isPointsAttached` is included for all fields — only the pointsGeoJSON
 * requirement reads it, so this is harmless for non-file fields and lets the
 * file and non-file loops share one context.
 */
function buildJobContext(job: Job, context: ValidationContext, dealMap: Map<string, Deal>): ValidationContext {
	const isCreatingNewSalesperson = job.salespersonIds?.includes(CREATE_NEW_SALESPERSON) ?? false;
	const dealId = job.dealIds?.[0];
	const deal: Deal | undefined = dealId ? dealMap.get(dealId) : undefined;
	return {
		...context,
		job,
		deal,
		isCreatingNewSalesperson,
		isPointsAttached: job.isPointsAttached,
		// The caller's context may already have a memoized _syncMethodCache (e.g. from a
		// prior syncMethodContains call against a different deal). Since `deal` above can
		// differ from context.deal, inheriting that cache would silently answer sync-method
		// questions against the wrong deal — invalidate it so it recomputes from this job's deal.
		_syncMethodCache: undefined,
	};
}

/**
 * Validate multiple jobs into structured per-job issues — the single source of
 * truth for validation. Each issue carries `blocking` (= isFieldVisible for the
 * field, evaluated with the same context validateField/validateFile use), so
 * gates can ignore errors on fields the user can't see/edit while displays can
 * still surface them. `validateJobs` (flat error map) and `classifyJobsValidity`
 * are both derived from this so the card, the NEXT gate, and the modal agree.
 */
export function validateJobsToIssues(jobs: Job[], context: ValidationContext): Record<string, JobIssue[]> {
	const dealMap = buildDealMap(context);
	const issuesByJob: Record<string, JobIssue[]> = {};

	for (const job of jobs) {
		const issues: JobIssue[] = [];
		const jobContext = buildJobContext(job, context, dealMap);

		// Standard (non-file) fields
		for (const fieldType of FIELD_TYPES_WITHOUT_FILES) {
			const value =
				fieldType === "salespersonMobilePhone"
					? job.newSalesperson?.phone // special case: nested in newSalesperson
					: (job as any)[FIELD_TO_PROPERTY_MAP[fieldType]];

			const fieldResult = validateField(fieldType, value, jobContext);
			if (!fieldResult.isValid) {
				issues.push({
					field: fieldType,
					message: fieldResult.error!,
					// An empty required value is "missing"; a present-but-malformed
					// value is "invalid". Derived from the value, not the message
					// string, so it stays correct even if a field omits a `required`
					// message and validateField uses its fallback string.
					kind: isEmpty(value, fieldType) ? "missing" : "invalid",
					blocking: isFieldVisible(fieldType, jobContext),
				});
			}
		}

		// File fields (validateFile only ever reports a missing required file).
		for (const fileType of FileTypes) {
			const fileResult = validateFile(fileType, job, jobContext);
			if (!fileResult.isValid) {
				const field = fileType as ImportTableColumn;
				issues.push({
					field,
					message: fileResult.error!,
					kind: "missing",
					blocking: isFieldVisible(field, jobContext),
				});
			}
		}

		issuesByJob[job.id] = issues;
	}

	return issuesByJob;
}

/**
 * Validate multiple jobs at once.
 * Returns an object with errors keyed by `${fieldType}-${jobId}` — ALL errors,
 * blocking and non-blocking. Derived from `validateJobsToIssues`; the external
 * contract is unchanged. Used by ImportTable.tsx and for per-field display.
 */
export function validateJobs(jobs: Job[], context: ValidationContext): Record<string, string> {
	const issuesByJob = validateJobsToIssues(jobs, context);
	const errors: Record<string, string> = {};
	for (const [jobId, issues] of Object.entries(issuesByJob)) {
		for (const issue of issues) {
			errors[getValidationErrorKey(issue.field, jobId)] = issue.message;
		}
	}
	return errors;
}

// #endregion

// #region REACT-HOOK-FORM INTEGRATION (for SingleImportForm)

/**
 * Get validation rules for react-hook-form's register() function
 * This converts our validation logic to react-hook-form's declarative format
 */
export function getFormValidationRules(
	fieldType: ImportTableColumn,
	context: ValidationContext,
): {
	required: string | boolean;
	pattern?: { value: RegExp; message: string };
	validate?: (value: any) => string | boolean;
} {
	const isRequired = isFieldRequired(fieldType, context);

	const rules: any = {
		required: isRequired
			? ValidationMessages[fieldType]?.required || `${columnDisplayNames[fieldType]} is required`
			: false,
	};

	// For fields with custom validation beyond "required", delegate to validateField
	const fieldsWithCustomValidation = [
		"growerPhoneNumber",
		"salespersonMobilePhone",
		"frequencyOfMicros",
		"depth",
		"density",
		"submitterEmail",
	];

	if (fieldsWithCustomValidation.includes(fieldType)) {
		rules.validate = (value: any) => {
			// Skip validation if empty and not required (let "required" rule handle it)
			if (!value && value !== 0) {
				return true;
			}

			// Delegate to centralized validation
			const result = validateField(fieldType, value, context);
			return result.isValid ? true : result.error!;
		};
	}

	return rules;
}
